// Apolo IDE — useStickToBottom: robust "follow the latest message" for a scroll
// container (the Code chat). Synthesized from 3 independent designs + their
// adversarial verification (workflow stick-to-bottom-design).
//
// The hard part is a chat that STREAMS: the answer grows token-by-token, and the
// user expects the view to keep tracking the latest line — but must NOT be yanked
// down while they scroll up to read history. The naive "scroll to bottom on a
// timer / on every onScroll" breaks because a SMOOTH programmatic scroll fires
// onScroll on every intermediate frame (still above the bottom), which clobbers
// the "follow" intent the button just engaged.
//
// Design — three signals, deliberately separate:
//   • followRef  — the persistent INTENT to stick to the bottom. The ONLY thing
//                  the streaming pin consults. A ref so callbacks read it fresh.
//   • showJump   — state: the user scrolled up, so show the "Ir pro fim" button.
//                  Toggled only on real user transitions (never per smooth frame),
//                  so the button doesn't flicker mid-animation.
//   • two guards — smoothUntilRef: while OUR button animation runs, intermediate
//                  (above-bottom) onScroll frames must not disengage follow.
//                  lastGestureRef: right after a user wheel/touch, the pin must
//                  not fire (a streamed token can't yank a reader who just moved).
//
// Why the pin needs NO guard window: it sets scrollTop = scrollHeight INSTANTLY,
// so its resulting onScroll always reports "at bottom" → it can never be misread
// as a user scrolling up. Only the button's smooth animation produces above-bottom
// frames, so only it gets a window. This is what kills the verifier-found
// "stuck button" + "swallowed wheel" bugs the timer-based guards had.

import { useCallback, useEffect, useRef, useState } from 'react';

const BOTTOM_THRESHOLD = 48; // px from the bottom that still counts as "at bottom"
const GESTURE_MS = 250; // after a user wheel/touch, the pin stays its hand this long
const SMOOTH_MS = 450; // safety cap on the button's smooth-animation guard window

function prefersReducedMotion() {
  try {
    return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * @param {{current: HTMLElement|null}} scrollRef  the overflow-y-auto container
 * @param {{current: HTMLElement|null}} contentRef the growing content inside it
 * @param {*} resetKey  changes (e.g. session id) re-pin + re-follow from the top
 * @returns {{ showJump: boolean, onScroll: () => void, scrollToBottom: () => void }}
 */
export default function useStickToBottom(scrollRef, contentRef, resetKey) {
  const followRef = useRef(true);
  const [showJump, setShowJump] = useState(false);
  const smoothUntilRef = useRef(0); // while Date.now() < this, OUR smooth scroll owns onScroll
  const lastGestureRef = useRef(0); // timestamp of the last user wheel/touch

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_THRESHOLD;

    // During OUR smooth scroll, intermediate frames sit above the bottom and must
    // not disengage follow or flash the button. The window ends the instant a
    // frame lands at the bottom (intent + geometry agree), or after SMOOTH_MS.
    if (Date.now() < smoothUntilRef.current) {
      if (nearBottom) smoothUntilRef.current = 0;
      return;
    }

    // Any other scroll is the USER (wheel, trackpad, scrollbar drag, keyboard):
    // follow iff they're at the bottom. Scrolling up disengages + shows the
    // button; returning to the bottom re-engages + hides it.
    followRef.current = nearBottom;
    setShowJump((prev) => (prev === !nearBottom ? prev : !nearBottom));
  }, [scrollRef]);

  // Instant pin to the bottom while following — keeps the latest streamed line in
  // view. Held back briefly after a user gesture so a token can't yank a reader.
  const pin = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !followRef.current) return;
    if (Date.now() - lastGestureRef.current < GESTURE_MS) return;
    el.scrollTop = el.scrollHeight;
  }, [scrollRef]);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    followRef.current = true; // engage persistent follow FIRST
    setShowJump(false); // hide the button immediately
    lastGestureRef.current = 0; // this is our scroll, not a user gesture
    const reduced = prefersReducedMotion();
    smoothUntilRef.current = Date.now() + (reduced ? 0 : SMOOTH_MS);
    el.scrollTo({ top: el.scrollHeight, behavior: reduced ? 'auto' : 'smooth' });
  }, [scrollRef]);

  // Mark user gestures so the streaming pin won't fight them (closes the 1-frame
  // race where a token's pin fires between a wheel and its scroll event).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const mark = () => { lastGestureRef.current = Date.now(); };
    el.addEventListener('wheel', mark, { passive: true });
    el.addEventListener('touchmove', mark, { passive: true });
    return () => {
      el.removeEventListener('wheel', mark);
      el.removeEventListener('touchmove', mark);
    };
  }, [scrollRef]);

  // Pin on content growth (new messages + streaming tokens) while following.
  useEffect(() => {
    const el = scrollRef.current;
    const content = contentRef.current;
    if (!el || !content || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => pin());
    ro.observe(content);
    return () => ro.disconnect();
  }, [scrollRef, contentRef, pin]);

  // New session / reset → start pinned + following.
  useEffect(() => {
    followRef.current = true;
    smoothUntilRef.current = 0;
    lastGestureRef.current = 0;
    setShowJump(false);
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [resetKey, scrollRef]);

  return { showJump, onScroll, scrollToBottom };
}
